import { createAsyncThunk } from '@reduxjs/toolkit';
import { MetricsActions } from 'application/constants/actions';
import { AxiosError } from 'axios';
import { MetricsAPI } from 'integration/api/metrics';
import {
  HealthMetricCreateRequest,
  HealthMetricUpdateRequest,
  UnitOfMeasurementCreateRequest,
} from '@docbay/schemas';

/**
 * Fetches metrics data from the API.
 *
 * @param params - An object containing pagination and filter parameters.
 * @returns The fetched metrics data.
 */
export const fetchMetrics = createAsyncThunk(
  MetricsActions.fetchMetrics,
  async (
    params: { page: number; limit: number; specializationIds?: string },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await MetricsAPI.fetchMetrics(params);

      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches specialization data from the API.
 *
 * @returns The fetched specialization data.
 */
export const fetchSpecializations = createAsyncThunk(
  MetricsActions.fetchSpecializations,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await MetricsAPI.fetchSpecializations();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches unit of measurement data from the API.
 *
 * @returns The fetched unit of measurement data.
 */
export const fetchUnitsOfMeasurement = createAsyncThunk(
  MetricsActions.fetchUnitsOfMeasurement,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await MetricsAPI.fetchUnitsOfMeasurement();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a new unit of measurement in the API.
 *
 * @param params - An object containing the details for the new unit of measurement.
 * @returns The created unit of measurement data.
 */
export const createUnitOfMeasurement = createAsyncThunk(
  MetricsActions.createUnitOfMeasurement,
  async (params: UnitOfMeasurementCreateRequest, { rejectWithValue }) => {
    try {
      const { data } = await MetricsAPI.createUnitOfMeasurement(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a unit of measurement from the API.
 *
 * @param id - The ID of the unit of measurement to delete.
 * @returns The deleted unit of measurement data.
 */
export const deleteUnitOfMeasurement = createAsyncThunk(
  MetricsActions.deleteUnitOfMeasurement,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await MetricsAPI.deleteUnitOfMeasurement(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      const rejectPayload = {
        statusCode: error?.response?.status,
        message: error.message,
      };

      return rejectWithValue(rejectPayload);
    }
  },
);

/**
 * Creates a new metric in the API.
 *
 * @param params - An object containing the details for the new metric.
 * @returns The created metric data.
 */
export const createMetric = createAsyncThunk(
  MetricsActions.createMetric,
  async (params: HealthMetricCreateRequest, { rejectWithValue }) => {
    try {
      const { data } = await MetricsAPI.createMetric(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a metric from the API.
 *
 * @param id - The ID of the metric to delete.
 * @returns The deleted metric data.
 */
export const deleteMetric = createAsyncThunk(
  MetricsActions.deleteMetric,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await MetricsAPI.deleteMetric(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Edits an existing metric in the API.
 *
 * @param params - An object containing the ID and details for the edited metric.
 * @returns The edited metric data.
 */
export const editMetric = createAsyncThunk(
  MetricsActions.editMetric,
  async (
    params: { id: string; data: HealthMetricUpdateRequest },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await MetricsAPI.editMetric(params.id, params.data);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
